/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useState } from 'react';
import { actions as helperActions } from 'Slice/HelperSlice';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import {
  maxMobile,
  maxTablet,
  primaryColor, primaryFont, primaryHeadingColor,
  scrollbar, secondaryFont, smMobile400, wLight, wMBold,
} from 'Utils/utilsStyle';
import GlobalButton from 'Components/common/components/GlobalButton';
import { useMsal } from './contexts/MsalContext';
import { loginRequest } from './config/authConfig';
import EventTimeSelector from './components/EventScheduler';

const Wrapper = styled.div`
.modal-header {
  margin: 0px;
  color: ${primaryHeadingColor};
  font-family: ${secondaryFont};
  font-size: 24px;
  font-weight: ${wMBold};
  letter-spacing: -0.344px;
}
.space-y-4 {
  .ant-card {
    &.ant-card-bordered {
      border: none;
    }
    .ant-card-body {
      color: ${primaryColor};
      font-family: ${primaryFont};
      font-size: 16px;
      font-weight: ${wLight};
      line-height: 28px;
      letter-spacing: -0.294px;
      padding: 8px 0px 24px 0px;
      @media all and (max-width: ${maxTablet}) {
        font-size: 12px;
      }
      .name-field {
        .ant-input {
          border-radius: 8px;
        }
      }
      .date-picker {
        .ant-picker {
          border-radius: 8px;
          padding: 4px 10px 4px 0px;
          .ant-picker-input {
            padding-left: 35px;
          }
          .ant-picker-suffix {
            position: absolute;
            left: 5px;
            .anticon-calendar {
              svg {
                height: 16px;
                width: 16px;
                fill: black;
                @media all and (max-width: ${maxMobile}) {
                  width: 14px;
                  height: 14px;
                }
              }
            }
          }
        }
      }
      .time-picker {
        width: 100%;
        .ant-space-item {
          &:nth-child(1), &:nth-child(3) {
            width: calc(50% - 5px);
            @media all and (max-width: ${smMobile400}) {
              width: calc(50% - 13px);
            }
          }
        }
        .ant-select-selector {
          padding-left: 35px;
          border-radius: 8px;
        }
        .ant-select-arrow {
          left: 0px;
          padding-left: 10px;
          .anticon-clock-circle {
            svg {
              height: 16px;
              width: 16px;
              fill: black;
              @media all and (max-width: ${maxMobile}) {
                width: 14px;
                height: 14px;
              }
            }
          }
        }
      }
      .recepients-form {
        position: relative;
        .add-icon {
          position: absolute;
          z-index: 1000;
          top: 11px;
          left: 10px;

        }
        .react-multi-email {
          border-radius: 8px;
          max-height: 150px;
          min-height: 150px;
          ${scrollbar};
           ::-webkit-scrollbar { width: 17px; } ::-webkit-scrollbar-track { background-color: transparent; } ::-webkit-scrollbar-thumb { background-color: #D9D9D9; border-radius: 20px; border: 7px solid transparent; background-clip: content-box; }
          &.empty {
            span {
              padding-left: 25px;
              font-size: 16px;
              line-height: 17px;
            }
          }
          input {
            padding-left: 25px !important;
          }
          div[role="button"] {
            margin-left: 25px;
          }
        }
      }
    }
  }
}
.btn-div {
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 8px;
}
`;
interface MsalAuthComponentProps {
  profileInfoEmail: string
  handleModalClose: () => void;
}

interface MeetindResponseData {
  '@odata.context': string;
  '@odata.etag': string;
  id: string;
  webLink: string;
}

export interface EventDataRequest {
  subject: string;
  start: Start;
  end: End;
  attendees: Attendee[];
}

export interface Start {
  dateTime: string;
  timeZone: string;
}

export interface End {
  dateTime: string;
  timeZone: string;
}

export interface Attendee {
  emailAddress: EmailAddress;
}

export interface EmailAddress {
  address: string;
}

const MsalAuthComponent = function ({
  profileInfoEmail,
  handleModalClose,
}: MsalAuthComponentProps) {
  const { instance, accounts } = useMsal();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRange, setSelectedRange] = useState<{ start: string; end: string } | null>(null);
  const [eventData, setEventData] = useState('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleTimeChange = (start: string, end: string) => {
    setSelectedRange({ start, end });
  };

  const validateDateTime = (dateTime: string): boolean => {
    const date = new Date(dateTime);
    return !Number.isNaN(date.getTime());
  };

  const isValidData = () => {
    const data: EventDataRequest = JSON.parse(eventData);
    const newErrors: { [key: string]: string } = {};

    if (!data.subject || data.subject.trim().length === 0) {
      newErrors.subject = 'Subject is required';
    }

    if (!data.attendees || data.attendees.length === 0) {
      newErrors.attendees = 'At least one attendee is required';
    }

    if (!validateDateTime(data.start.dateTime)) {
      newErrors.startDateTime = 'Invalid start date and time';
    }

    if (!validateDateTime(data.end.dateTime)) {
      newErrors.endDateTime = 'Invalid end date and time';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      console.log('Validation errors:', newErrors);
      return false;
    }

    setErrors({});
    return true;
  };

  const fetchCalendarEvents = async (accessToken: string) => {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;
    headers.append('Authorization', bearer);
    headers.append('Content-Type', 'application/json');

    const options = {
      method: 'POST',
      headers,
      body: eventData,
    };

    try {
      const response = await fetch('https://graph.microsoft.com/v1.0/me/events?$select=webLink', options);
      if (response.ok) {
        const data: MeetindResponseData = await response.json();
        dispatch(helperActions.setNotification({
          message: 'Event scheduled successfully',
          proTip: false,
          rightPlacement: false,
          groupId: 0,
          classnameFlag: true,
          type: 'Meeting',
          meetingLink: data?.webLink,
        }));
      }
    } catch (error) {
      console.error('Error fetching calendar events:', error);
    } finally {
      handleModalClose();
    }
  };

  const handleGetEvents = async () => {
    if (isValidData()) {
      setIsLoading(true);
      if (instance) {
        try {
          let accessToken;
          if (accounts.length > 0) {
            const response = await instance.acquireTokenSilent({
              ...loginRequest,
              account: accounts[0],
            });
            accessToken = response.accessToken;
          } else {
            const loginResponse = await instance.loginPopup(loginRequest);
            accessToken = loginResponse.accessToken;
          }

          await fetchCalendarEvents(accessToken);
        } catch (error) {
          console.error('Error acquiring token or fetching events:', error);
          try {
            const loginResponse = await instance.loginPopup(loginRequest);
            await fetchCalendarEvents(loginResponse.accessToken);
          } catch (loginError) {
            console.error('Login failed', loginError);
          }
        } finally {
          setIsLoading(false);
        }
      }
    } else {
      console.log('Errors');
    }
  };

  return (
    <Wrapper>
      <div className="w-[350px] flex flex-col items-center justify-center">
        <h1 className="modal-header">Add Schedule</h1>

        {/* Display Error Messages */}
        {Object.keys(errors).length > 0 && (
          <div className="mb-4">
            {Object.entries(errors).map(([key, message]) => (
              <div key={key} className="text-red-500">{message}</div>
            ))}
          </div>
        )}

        <div className="space-y-4">
          <EventTimeSelector
            onTimeChange={handleTimeChange}
            selectedRange={selectedRange}
            setEventData={setEventData}
            profileInfoEmail={profileInfoEmail}
          />
        </div>
        <div className="btn-div">
          <GlobalButton
            title="Cancel"
            outlineColor={primaryColor}
            bgColor="#FFF"
            innerSpace="9px 30px 9px 30px"
            textColor={primaryColor}
            onButtonClick={handleModalClose}
            borderRadius="40px"
            height="37px"
          />
          <GlobalButton
            title="Send"
            outlineColor={primaryColor}
            bgColor={primaryColor}
            innerSpace="9px 30px 9px 30px"
            textColor="#FFF"
            onButtonClick={handleGetEvents}
            disabled={isLoading}
            borderRadius="40px"
            height="37px"
          />

        </div>
      </div>
    </Wrapper>
  );
};

export default MsalAuthComponent;
